import { db } from '../lib/firebase';
import { collection, query, orderBy, limit, getDocs, where, onSnapshot, addDoc, doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';

// Database structure: top-level collections
// const VINDIE_COLLECTION = 'vindie'; // Reverted - Firestore doesn't support this pattern

export interface ContentItem {
  id: string;
  title: string;
  description: string;
  category: 'movie' | 'series' | 'short' | 'comic' | 'novel' | 'book' | 'anime';
  thumbnail: string;
  poster?: string;
  backdrop?: string;
  trailer?: string;
  rating: number;
  views: number;
  likes: number;
  dislikes: number;
  uploader: string;
  uploaderId: string;
  uploaderAvatar?: string;
  genres: string[];
  language: string;
  contentRating: string;
  duration?: number; // in minutes
  year?: number;
  createdAt: Date;
  updatedAt: Date;
  status: 'published' | 'draft' | 'hidden' | 'removed';
  isPublic: boolean;
  tags?: string[] | string;
  cast?: string[];
  director?: string[];
  writer?: string[];
  assets?: {
    [key: string]: string | string[];
  };
  episodeNumber?: string;
  seasonNumber?: string;
  linkedContent?: string;
  commentsEnabled?: boolean;
  ageRestriction?: boolean;
  donationRequired?: boolean;
}

export interface ContinueWatchingItem extends ContentItem {
  progress: number; // 0-100
  currentEpisode?: number;
  currentSeason?: number;
  lastWatchedAt: Date;
  watchTime: number; // in seconds
}

export interface SubscribedCreator {
  id: string;
  name: string;
  avatar?: string;
  followers: number;
  verified: boolean;
  isOnline: boolean;
  lastActive: Date;
  newContentCount: number;
}

export const getContent = async (
  category?: string,
  limitCount: number = 20,
  orderByField: string = 'views'
): Promise<ContentItem[]> => {
  try {
    let q = query(collection(db, 'content'), where('status', '==', 'published'), where('isPublic', '==', true));
    
    if (category && category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    q = query(q, orderBy(orderByField, 'desc'), limit(limitCount));
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting content:', error);
    throw error;
  }
};

export const getContinueWatching = (
  userId: string,
  callback: (items: ContinueWatchingItem[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'users', userId, 'continueWatching'),
    orderBy('lastWatchedAt', 'desc'),
    limit(20)
  );
  
  return onSnapshot(q, 
    (snapshot) => {
      const items: ContinueWatchingItem[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        items.push({
          ...data,
          lastWatchedAt: data.lastWatchedAt?.toDate() || new Date(),
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ContinueWatchingItem);
      });
      callback(items);
    },
    (error) => {
      console.error('Continue watching listener error:', error);
      // Don't throw the error, just log it
    }
  );
};

export const getSubscribedCreators = (
  userId: string,
  callback: (creators: SubscribedCreator[]) => void
): (() => void) => {
  const q = query(
    collection(db, 'users', userId, 'subscriptions'),
    orderBy('subscribedAt', 'desc'),
    limit(20)
  );
  
  return onSnapshot(q, 
    (snapshot) => {
      const creators: SubscribedCreator[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        creators.push({
          id: doc.id,
          ...data,
          lastActive: data.lastActive?.toDate() || new Date()
        } as SubscribedCreator);
      });
      callback(creators);
    },
    (error) => {
      console.error('Subscribed creators listener error:', error);
      // Don't throw the error, just log it
    }
  );
};

export const getTopContent = async (
  category: string = 'all',
  timeFrame: 'week' | 'month' | 'year' | 'all' = 'all',
  limitCount: number = 50
): Promise<ContentItem[]> => {
  try {
    let q = query(collection(db, 'content'), where('status', '==', 'published'), where('isPublic', '==', true));
    
    if (category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    // Filter by time frame
    if (timeFrame !== 'all') {
      const now = new Date();
      let startDate: Date;
      
      switch (timeFrame) {
        case 'week':
          startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          break;
        case 'month':
          startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          break;
        case 'year':
          startDate = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
          break;
        default:
          startDate = new Date(0);
      }
      
      q = query(q, where('createdAt', '>=', startDate));
    }
    
    q = query(q, orderBy('views', 'desc'), limit(limitCount));
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting top content:', error);
    throw error;
  }
};

export const getRecentUploads = async (limitCount: number = 20): Promise<ContentItem[]> => {
  try {
    const q = query(
      collection(db, 'content'),
      where('status', '==', 'published'),
      where('isPublic', '==', true),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting recent uploads:', error);
    throw error;
  }
};

/**
 * Get most popular content by views for a specific category
 */
export const getMostPopularContent = async (
  category: string,
  limitCount: number = 10
): Promise<ContentItem[]> => {
  try {
    let q = query(
      collection(db, 'content'),
      where('status', '==', 'published'),
      where('isPublic', '==', true),
      where('views', '>=', 1)
    );
    
    if (category && category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    q = query(q, orderBy('views', 'desc'), limit(limitCount));
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting most popular content:', error);
    throw error;
  }
};

/**
 * Get best rated content with minimum views threshold
 * Note: Firestore only allows one range filter, so we filter rating > 0 in memory
 */
export const getBestRatedContent = async (
  category: string,
  limitCount: number = 10,
  minViews: number = 10
): Promise<ContentItem[]> => {
  try {
    let q = query(
      collection(db, 'content'),
      where('status', '==', 'published'),
      where('isPublic', '==', true),
      where('views', '>=', minViews)
    );
    
    if (category && category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    q = query(q, orderBy('rating', 'desc'), limit(limitCount * 2)); // Get more to filter by rating
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const rating = data.rating || 0;
      
      // Filter content with rating > 0 in memory
      if (rating > 0) {
        content.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ContentItem);
      }
    });
    
    // Return top rated content, already sorted by rating desc
    return content.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting best rated content:', error);
    throw error;
  }
};

/**
 * Get latest content for a specific category
 */
export const getLatestContent = async (
  category: string,
  limitCount: number = 10
): Promise<ContentItem[]> => {
  try {
    let q = query(
      collection(db, 'content'),
      where('status', '==', 'published'),
      where('isPublic', '==', true)
    );
    
    if (category && category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    q = query(q, orderBy('createdAt', 'desc'), limit(limitCount));
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting latest content:', error);
    throw error;
  }
};

/**
 * Get hidden gems - content with low views but high engagement (good likes/views ratio)
 * Note: Firestore only allows one range filter, so we filter likes >= minLikes in memory
 */
export const getHiddenGemsContent = async (
  category: string,
  limitCount: number = 10,
  maxViews: number = 50,
  minLikes: number = 3
): Promise<ContentItem[]> => {
  try {
    // Get content with low views (using only one range filter)
    let q = query(
      collection(db, 'content'),
      where('status', '==', 'published'),
      where('isPublic', '==', true),
      where('views', '<=', maxViews)
    );
    
    if (category && category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    // Order by createdAt to get recent hidden gems
    q = query(q, orderBy('createdAt', 'desc'), limit(limitCount * 3)); // Get more to filter by likes and engagement ratio
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const views = data.views || 0;
      const likes = data.likes || 0;
      
      // Filter content with minimum likes in memory
      if (likes >= minLikes) {
        // Calculate engagement ratio (likes per view)
        const engagementRatio = views > 0 ? likes / views : 0;
        
        // Only include content with good engagement ratio (at least 0.1 or 10%)
        if (engagementRatio >= 0.1 || likes >= minLikes) {
          content.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date()
          } as ContentItem);
        }
      }
    });
    
    // Sort by engagement ratio (likes/views) descending, then limit
    content.sort((a, b) => {
      const aRatio = a.views > 0 ? a.likes / a.views : 0;
      const bRatio = b.views > 0 ? b.likes / b.views : 0;
      return bRatio - aRatio;
    });
    
    return content.slice(0, limitCount);
  } catch (error) {
    console.error('Error getting hidden gems content:', error);
    throw error;
  }
};

export const getContentByGenre = async (
  genre: string,
  limitCount: number = 20,
  category?: string
): Promise<ContentItem[]> => {
  try {
    // Normalize genre - capitalize first letter to match how genres are stored
    const normalizedGenre = genre === 'all' ? '' : genre.charAt(0).toUpperCase() + genre.slice(1).toLowerCase();
    
    let q = query(
      collection(db, 'content'),
      where('status', '==', 'published'),
      where('isPublic', '==', true)
    );
    
    // Only add genre filter if not 'all'
    if (normalizedGenre && normalizedGenre !== 'all') {
      q = query(q, where('genres', 'array-contains', normalizedGenre));
    }
    
    if (category && category !== 'all') {
      q = query(q, where('category', '==', category));
    }
    
    q = query(q, orderBy('views', 'desc'), limit(limitCount));
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting content by genre:', error);
    // If orderBy fails (no index), try without it
    try {
      let q = query(
        collection(db, 'content'),
        where('status', '==', 'published'),
        where('isPublic', '==', true)
      );
      
      const normalizedGenre = genre === 'all' ? '' : genre.charAt(0).toUpperCase() + genre.slice(1).toLowerCase();
      if (normalizedGenre && normalizedGenre !== 'all') {
        q = query(q, where('genres', 'array-contains', normalizedGenre));
      }
      
      if (category && category !== 'all') {
        q = query(q, where('category', '==', category));
      }
      
      q = query(q, limit(limitCount));
      
      const querySnapshot = await getDocs(q);
      const content: ContentItem[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        content.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ContentItem);
      });
      
      // Sort by views manually if orderBy failed
      return content.sort((a, b) => (b.views || 0) - (a.views || 0));
    } catch (fallbackError) {
      console.error('Error in fallback genre query:', fallbackError);
      throw error;
    }
  }
};

export const getContentById = async (contentId: string): Promise<ContentItem | null> => {
  try {
    const docRef = doc(db, 'content', contentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem;
    } else {
      return null;
    }
  } catch (error) {
    console.error('Error getting content by ID:', error);
    throw error;
  }
};

export const getContentByIds = async (contentIds: string[]): Promise<ContentItem[]> => {
  try {
    if (contentIds.length === 0) return [];
    
    const content: ContentItem[] = [];
    
    // Firestore 'in' queries are limited to 10 items, so we need to batch them
    const batchSize = 10;
    for (let i = 0; i < contentIds.length; i += batchSize) {
      const batch = contentIds.slice(i, i + batchSize);
      const q = query(
        collection(db, 'content'),
        where('__name__', 'in', batch),
        where('status', '==', 'published'),
        where('isPublic', '==', true)
      );
      
      const querySnapshot = await getDocs(q);
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        content.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        } as ContentItem);
      });
    }
    
    return content;
  } catch (error) {
    console.error('Error getting content by IDs:', error);
    throw error;
  }
};

export const listOwnerSeries = async (userId: string): Promise<ContentItem[]> => {
  try {
    const q = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId),
      where('category', '==', 'series'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting owner series:', error);
    throw error;
  }
};

export const listOwnerComics = async (userId: string): Promise<ContentItem[]> => {
  try {
    const q = query(
      collection(db, 'content'),
      where('uploaderId', '==', userId),
      where('category', '==', 'comic'),
      orderBy('createdAt', 'desc')
    );
    
    const querySnapshot = await getDocs(q);
    const content: ContentItem[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      content.push({
        id: doc.id,
        ...data,
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date()
      } as ContentItem);
    });
    
    return content;
  } catch (error) {
    console.error('Error getting owner comics:', error);
    throw error;
  }
};

export const addEpisodeToSeries = async (
  seriesId: string,
  episodeData: {
    title: string;
    description: string;
    videoUrl: string;
    thumbnail: string;
    duration: number;
    episodeNumber: number;
    seasonNumber: number;
  }
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'content'), {
      title: episodeData.title,
      description: episodeData.description,
      category: 'episode',
      visibility: 'public',
      rating: 0,
      language: '',
      genres: [],
      assets: { video: episodeData.videoUrl, thumbnail: episodeData.thumbnail },
      linkedContent: seriesId,
      seasonNumber: String(episodeData.seasonNumber),
      episodeNumber: String(episodeData.episodeNumber),
      uploaderId: '',
      status: 'published',
      isPublic: true,
      views: 0,
      likes: 0,
      dislikes: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding episode to series:', error);
    throw error;
  }
};

// Compute next numeric episode number for a given series season
export const getNextEpisodeNumber = async (
  seriesId: string,
  seasonNumber: string
): Promise<number> => {
  try {
    const episodesQuery = query(
      collection(db, 'content'),
      where('category', '==', 'episode'),
      where('linkedContent', '==', seriesId),
      where('seasonNumber', '==', seasonNumber)
    );
    const snapshot = await getDocs(episodesQuery);
    let maxNum = 0;
    snapshot.forEach((docSnap) => {
      const data = docSnap.data() as any;
      const n = parseInt(String(data.episodeNumber || '0'), 10);
      if (!isNaN(n)) maxNum = Math.max(maxNum, n);
    });
    return maxNum + 1;
  } catch (error) {
    console.error('Error computing next episode number:', error);
    return 1;
  }
};

export const addChapterToComic = async (
  comicId: string,
  chapterData: {
    title: string;
    description: string;
    fileUrl: string;
    thumbnail: string;
    chapterNumber: number;
    pages: number;
  }
): Promise<string> => {
  try {
    const docRef = await addDoc(collection(db, 'content'), {
      ...chapterData,
      comicId,
      category: 'chapter',
      uploaderId: '', // Will be set by the calling component
      status: 'published',
      isPublic: true,
      views: 0,
      likes: 0,
      dislikes: 0,
      rating: 0,
      createdAt: new Date(),
      updatedAt: new Date()
    });
    
    return docRef.id;
  } catch (error) {
    console.error('Error adding chapter to comic:', error);
    throw error;
  }
};

// Get episodes for a series
export const getEpisodesForSeries = async (seriesId: string): Promise<ContentItem[]> => {
  try {
    const episodesQuery = query(
      collection(db, 'content'),
      where('category', '==', 'episode'),
      where('linkedContent', '==', seriesId)
    );
    
    const episodesSnapshot = await getDocs(episodesQuery);
    const episodes: ContentItem[] = [];
    
    episodesSnapshot.forEach((doc) => {
      const data = doc.data();
      episodes.push({
        id: doc.id,
        title: data.title || '',
        description: data.description || '',
        category: data.category || 'episode',
        thumbnail: data.thumbnail || '',
        poster: data.poster || '',
        backdrop: data.backdrop || '',
        trailer: data.trailer || '',
        rating: data.rating || 0,
        views: data.views || 0,
        likes: data.likes || 0,
        dislikes: data.dislikes || 0,
        uploader: data.uploader || '',
        uploaderId: data.uploaderId || '',
        uploaderAvatar: data.uploaderAvatar || '',
        genres: data.genres || [],
        language: data.language || '',
        contentRating: data.contentRating || '',
        duration: data.duration || 0,
        year: data.year || new Date().getFullYear(),
        createdAt: data.createdAt?.toDate() || new Date(),
        updatedAt: data.updatedAt?.toDate() || new Date(),
        status: data.status || 'published',
        isPublic: data.isPublic !== false,
        tags: data.tags || [],
        cast: data.cast || [],
        director: data.director || [],
        writer: data.writer || [],
        assets: data.assets || {},
        episodeNumber: data.episodeNumber || '',
        seasonNumber: data.seasonNumber || '',
        linkedContent: data.linkedContent || ''
      });
    });
    
    // Sort episodes by episode number
    episodes.sort((a, b) => {
      const aNum = parseInt((a as any).episodeNumber || '0');
      const bNum = parseInt((b as any).episodeNumber || '0');
      return aNum - bNum;
    });
    
    return episodes;
  } catch (error) {
    console.error('Error fetching episodes for series:', error);
    throw error;
  }
};

// Get seasons for a series
export const getSeasonsForSeries = async (seriesId: string): Promise<{ seasonNumber: string; episodeCount: number; episodes: ContentItem[] }[]> => {
  try {
    const episodes = await getEpisodesForSeries(seriesId);
    
    // Group episodes by season
    const seasonsMap = new Map<string, ContentItem[]>();
    
    episodes.forEach(episode => {
      const seasonNum = episode.seasonNumber || '1';
      if (!seasonsMap.has(seasonNum)) {
        seasonsMap.set(seasonNum, []);
      }
      seasonsMap.get(seasonNum)!.push(episode);
    });
    
    // Convert to array and sort by season number
    const seasons = Array.from(seasonsMap.entries()).map(([seasonNumber, episodes]) => ({
      seasonNumber,
      episodeCount: episodes.length,
      episodes: episodes.sort((a, b) => {
        const aNum = parseInt(a.episodeNumber || '0');
        const bNum = parseInt(b.episodeNumber || '0');
        return aNum - bNum;
      })
    })).sort((a, b) => parseInt(a.seasonNumber) - parseInt(b.seasonNumber));
    
    return seasons;
  } catch (error) {
    console.error('Error getting seasons for series:', error);
    throw error;
  }
};

// Upload book content to Firebase
export const uploadBook = async (bookData: {
  title: string;
  description: string;
  author: string;
  genre: string;
  language: string;
  contentRating: string;
  tags: string[];
  coverFile?: File;
  bookFile?: File;
  uploaderId: string;
  uploaderName: string;
}): Promise<string> => {
  try {
    const contentData = {
      title: bookData.title,
      description: bookData.description,
      category: 'book' as const,
      thumbnail: '', // Will be set after cover upload
      rating: 0,
      views: 0,
      likes: 0,
      dislikes: 0,
      uploader: bookData.uploaderName,
      uploaderId: bookData.uploaderId,
      genres: [bookData.genre],
      language: bookData.language,
      contentRating: bookData.contentRating,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'published' as const,
      isPublic: true,
      tags: bookData.tags,
      author: bookData.author,
      assets: {
        bookFile: bookData.bookFile?.name || '',
        coverFile: bookData.coverFile?.name || ''
      }
    };

    const docRef = await addDoc(collection(db, 'content'), contentData);
    return docRef.id;
  } catch (error) {
    console.error('Error uploading book:', error);
    throw error;
  }
};

// Update content function
export const updateContent = async (
  contentId: string, 
  updateData: any, 
  assetUrls?: Record<string, string | string[]>
) => {
  try {
    const contentRef = doc(db, 'content', contentId);
    const updatePayload: any = {
      ...updateData,
      updatedAt: serverTimestamp()
    };

    // Add asset URLs if provided
    if (assetUrls) {
      updatePayload.assets = {
        ...updateData.assets,
        ...assetUrls
      };
    }

    await updateDoc(contentRef, updatePayload);
  } catch (error) {
    console.error('Error updating content:', error);
    throw error;
  }
};

// Legacy type exports for compatibility
export type Content = ContentItem;
export type SeriesDoc = ContentItem;
export type ComicDoc = ContentItem;